import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

const COPY = {
  vision_limit: {
    heading: "You've used all 3 free Visions",
    body: 'Upgrade to Pro for unlimited Visions and AI advice tailored to your exact numbers.',
  },
  connect_accounts: {
    heading: 'Make your plan more accurate',
    body: 'Connect your bank accounts with Pro to give Aura real data — and get advice that actually fits your life.',
  },
  strategy_card: {
    heading: 'Your 3-month plan is ready',
    body: "Upgrade to Pro to unlock your full Vision strategy with monthly milestones and Aura's direct verdict.",
  },
  book_advisor: {
    heading: 'Talk to a real advisor',
    body: 'Upgrade to Pro+ to book a free monthly consultation with a verified financial advisor — matched to your Vision.',
  },
  share_vision: {
    heading: 'Share your Vision progress',
    body: 'Upgrade to Pro to share your Vision card with friends and celebrate milestones.',
  },
};

const PRO_FEATURES = [
  'Unlimited Visions',
  'AI advice on your real numbers',
  '3-month Vision strategies',
  'Connect your bank accounts',
];

const PRO_PLUS_FEATURES = [
  'Everything in Pro',
  '1 free advisor consultation/month',
  'Priority AI responses',
];

const UpgradeModal = ({ feature_blocked, on_close }) => {
  const navigate = useNavigate();
  const copy = COPY[feature_blocked] || COPY.strategy_card;
  const showProPlus = feature_blocked === 'book_advisor';

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') on_close?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [on_close]);

  const goUpgrade = (tier) => {
    on_close?.();
    navigate(`/upgrade?tier=${tier}`);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center pointer-events-none">
        {/* Desktop: light overlay; mobile: blur only */}
        <motion.button
          type="button"
          aria-label="Close"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={on_close}
          className="absolute inset-0 pointer-events-auto bg-ink/20 md:bg-ink/30 backdrop-blur-sm md:backdrop-blur-none"
        />

        <motion.div
          role="dialog"
          aria-modal="true"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="pointer-events-auto relative w-full max-w-lg md:max-w-md mx-auto md:mx-4 bg-bone rounded-t-2xl md:rounded-2xl border-t md:border border-pine shadow-lg max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6 md:p-8">
            <div className="flex justify-end md:absolute md:top-4 md:right-4">
              <button type="button" onClick={on_close} className="icon-btn" aria-label="Close">
                <XMarkIcon className="w-5 h-5 text-ink-muted" />
              </button>
            </div>

            <h2 className="font-display text-h2 text-ink mb-2 pr-8">{copy.heading}</h2>
            <p className="text-body text-ink-muted mb-6">{copy.body}</p>

            <div className={`space-y-4 ${showProPlus ? '' : ''}`}>
              <div className="rounded-xl bg-pine p-5 text-bone-raised">
                <p className="text-label font-medium mb-1">Aura Pro</p>
                <p className="text-body mb-4 opacity-90">$9.99/month or $79/year</p>
                <ul className="space-y-2 mb-5">
                  {PRO_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-body">
                      <CheckIcon className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => goUpgrade('pro')}
                  className="w-full py-3 rounded-md bg-bone-raised text-pine font-medium text-body hover:opacity-95 transition-opacity"
                >
                  Upgrade to Pro
                </button>
              </div>

              {showProPlus && (
                <div className="rounded-xl bg-ink p-5 text-bone-raised">
                  <p className="text-label font-medium mb-1">Aura Pro+</p>
                  <p className="text-body mb-4 opacity-90">$19.99/month</p>
                  <ul className="space-y-2 mb-5">
                    {PRO_PLUS_FEATURES.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-body">
                        <CheckIcon className="w-4 h-4 shrink-0 mt-0.5 text-pine" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => goUpgrade('pro_plus')}
                    className="w-full py-3 rounded-md bg-pine text-bone-raised font-medium text-body hover:opacity-95 transition-opacity"
                  >
                    Upgrade to Pro+
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={on_close}
              className="mt-6 w-full text-center text-body text-ink-muted hover:text-ink transition-colors"
            >
              Maybe later
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UpgradeModal;
